
import { supabase } from "@/integrations/supabase/client";
import { 
  ModalCampaignUserLog, 
  ModalCampaignUserQuizResult, 
  CampaignStatistics, 
  UserViewData 
} from "@/types/modalCampaigns";

/**
 * Get view logs for a specific campaign
 */
export const getCampaignViewLogs = async (campaignId: string): Promise<ModalCampaignUserLog[]> => {
  const { data, error } = await supabase
    .from('modal_campaign_user_logs')
    .select('*, users:user_id(id, email)')
    .eq('campaign_id', campaignId)
    .order('displayed_at', { ascending: false });

  if (error) {
    console.error('Error fetching campaign logs:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get quiz results for a specific campaign
 */
export const getCampaignQuizResults = async (campaignId: string): Promise<ModalCampaignUserQuizResult[]> => {
  const { data, error } = await supabase
    .from('modal_campaign_user_quiz_results')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('completed_at', { ascending: false });

  if (error) {
    console.error('Error fetching quiz results:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get aggregated statistics for a campaign
 */
export const getCampaignStatistics = async (campaignId: string): Promise<CampaignStatistics> => {
  // Get basic campaign info
  const { data: campaign, error: campaignError } = await supabase
    .from('modal_campaigns')
    .select('*')
    .eq('id', campaignId)
    .single();

  if (campaignError) {
    console.error('Error fetching campaign:', campaignError);
    throw campaignError;
  }

  // Get all view logs
  const logs = await getCampaignViewLogs(campaignId);
  
  // Get all quiz results if the campaign has a quiz
  let quizResults: ModalCampaignUserQuizResult[] = [];
  if (campaign.has_quiz) {
    quizResults = await getCampaignQuizResults(campaignId);
  }

  // Get user data
  const userIds = Array.from(new Set(logs.map(log => log.user_id)));
  const { data: users, error: usersError } = await supabase
    .from('medewerkers')
    .select('id, naam')
    .in('id', userIds);

  if (usersError) {
    console.error('Error fetching users:', usersError);
    // Continue without user data
  }

  const userMap = (users || []).reduce((acc, user) => {
    acc[user.id] = user.naam;
    return acc;
  }, {} as Record<string, string>);

  // Process data for statistics
  const totalViews = logs.length;
  const uniqueViewers = new Set(logs.map(log => log.user_id)).size;
  
  // Calculate view data by day
  const viewsByDay: Record<string, number> = {};
  logs.forEach(log => {
    const date = new Date(log.displayed_at).toISOString().split('T')[0];
    viewsByDay[date] = (viewsByDay[date] || 0) + 1;
  });
  
  // Calculate dismissal rate
  const dismissedLogs = logs.filter(log => log.dismissed_at !== null);
  const dismissalRate = totalViews > 0 ? dismissedLogs.length / totalViews : 0;
  
  // Calculate permanent dismissal rate
  const permanentDismissals = logs.filter(log => log.dismissed_permanently === true);
  const permanentDismissalRate = totalViews > 0 ? permanentDismissals.length / totalViews : 0;

  // Quiz statistics
  const totalQuizAttempts = quizResults.length;
  const uniqueQuizParticipants = new Set(quizResults.map(result => result.user_id)).size;
  const passedQuizzes = quizResults.filter(result => result.passed).length;
  const passRate = totalQuizAttempts > 0 ? passedQuizzes / totalQuizAttempts : 0;
  const averageScore = totalQuizAttempts > 0 
    ? quizResults.reduce((sum, result) => sum + result.score, 0) / totalQuizAttempts 
    : 0;
  
  // Get user view data
  const userViewData: UserViewData[] = userIds.map(userId => {
    const userLogs = logs.filter(log => log.user_id === userId);
    const userQuizResults = quizResults.filter(result => result.user_id === userId);
    
    const lastViewed = userLogs.length > 0 
      ? new Date(Math.max(...userLogs.map(log => new Date(log.displayed_at).getTime())))
      : null;
      
    return {
      userId,
      userName: userMap[userId] || userId,
      viewCount: userLogs.length,
      lastViewed: lastViewed ? lastViewed.toISOString() : null,
      dismissed: userLogs.some(log => log.dismissed_at !== null),
      dismissedPermanently: userLogs.some(log => log.dismissed_permanently === true),
      quizAttempts: userQuizResults.length,
      quizPassed: userQuizResults.some(result => result.passed),
      highestScore: userQuizResults.length > 0 
        ? Math.max(...userQuizResults.map(result => result.score))
        : null,
    };
  });

  // Prepare the dailyViewData for chart
  const dailyViewData = Object.entries(viewsByDay)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
  
  return {
    campaignId,
    campaignTitle: campaign.title,
    totalViews,
    uniqueViewers,
    dismissalRate,
    permanentDismissalRate,
    hasQuiz: campaign.has_quiz,
    totalQuizAttempts,
    uniqueQuizParticipants,
    passRate,
    averageScore,
    dailyViewData,
    userViewData,
  };
};
